import { ActivityNames, CaseActor, CaseReport, CaseReportSummary } from '../models/CaseTypes';

// Display labels for all activity types
const ACTIVITY_LABEL: Record<string, string> = {
  [ActivityNames.EXPOSURE]: 'Exposure',
  [ActivityNames.ANATOMY_LOCALIZATION]: 'Anatomy Localization',
  [ActivityNames.CANNULATION_SCREW_PLACEMENT]: 'Cannulation/Screw Placement',
  [ActivityNames.CANNULATION]: 'Cannulation',
  [ActivityNames.SCREW_PLACEMENT]: 'Screw Placement',
  [ActivityNames.DECOMPRESSION_ALIGNMENT]: 'Decompression/Alignment',
  [ActivityNames.CLOSURE]: 'Closure',
  [ActivityNames.POST_CANNULATION]: 'Post Cannulation Activity',
  [ActivityNames.ACCEPTED_REGISTRATION]: 'Accepted Registration',
  [ActivityNames.REJECTED_REGISTRATION]: 'Rejected Registration',
  [ActivityNames.INTRAOP_MEASUREMENT]: 'Measurements Taken',
};

// Color mapping using CSS variables (keyed by ActivityNames)
const ACTIVITY_COLORS: Record<string, string> = {
  [ActivityNames.EXPOSURE]: 'var(--C7-Exposure)',
  [ActivityNames.ANATOMY_LOCALIZATION]: 'var(--C4)',
  [ActivityNames.CANNULATION_SCREW_PLACEMENT]: 'var(--L3-Cannulation-Screws)',
  [ActivityNames.CANNULATION]: 'var(--L3-Cannulation-Screws)',
  [ActivityNames.SCREW_PLACEMENT]: 'var(--C2)',
  [ActivityNames.DECOMPRESSION_ALIGNMENT]: 'var(--Decompression)',
  [ActivityNames.CLOSURE]: 'var(--Closure)',
  [ActivityNames.POST_CANNULATION]: 'var(--S1)',
  [ActivityNames.ACCEPTED_REGISTRATION]: 'var(--chart-2)',
  [ActivityNames.REJECTED_REGISTRATION]: 'var(--chart-4)',
  [ActivityNames.INTRAOP_MEASUREMENT]: 'var(--T2)',
};

// Legacy hex colors for non-chart contexts (keyed by ActivityNames).
// TODO: Remove once we upgrade to v1.2 and have removed `getStepColor`
const STEP_COLORS_LEGACY: Record<string, string> = {
  [ActivityNames.EXPOSURE]: '#00C19B',
  [ActivityNames.CANNULATION_SCREW_PLACEMENT]: '#4C9FFF',
  [ActivityNames.ACCEPTED_REGISTRATION]: '#00FF00',
  [ActivityNames.REJECTED_REGISTRATION]: '#FF0000',
  [ActivityNames.DECOMPRESSION_ALIGNMENT]: '#D6A32C',
  [ActivityNames.CLOSURE]: '#00BCD8',
  [ActivityNames.INTRAOP_MEASUREMENT]: '#8E3DBC',
  [ActivityNames.POST_CANNULATION]: '#D6A32C',
};

/**
 * Formats the case date for display. Day portion is intentionally omitted.
 *
 * @param date
 * @param numericFormat
 * @returns Formatted date string.
 */
export const formatCaseDateDisplay = (date: Date, numericFormat: boolean = false): string => {
  return numericFormat ?
    date.toLocaleDateString(undefined, { year: '2-digit', month: '2-digit', timeZone: 'UTC' }) :
    date.toLocaleDateString(undefined, { year: '2-digit', month: 'short', timeZone: 'UTC' });
};

export const formatTimeDisplay = (timeMins: number|undefined, dense: boolean = false): string => {
  if (!timeMins) return dense ? '0m' : '0mins';
  const hours = Math.floor(timeMins / 60);
  const minutes = Math.floor(timeMins % 60);

  if (dense) {
    if (hours > 0) {
      return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
    }
    return `${minutes}m`;
  }

  if (hours > 0) {
    return `${hours} hr ${minutes > 0 ? minutes + ' min' : ''}`.trim();
  }
  return `${minutes} mins`;
};

/***
 * Formats the case description based on procedures or levels.
 * If procedures exist, formats as "locusStart-locusEnd procedureType" for each procedure,
 * joined by " and ". If no procedures, uses levels and case name.
 *
 * @param caseSummary The summary of the case report.
 * @returns Formatted case description string.
 */
export const formatCaseDescription = (caseSummary: CaseReportSummary|CaseReport, shortForm: boolean): string => {
  let caseDescription = '';

  // Format procesures
  if (caseSummary.procedures && caseSummary.procedures.length > 0) {
    caseDescription = caseSummary.procedures.map(proc => {
      return proc.locusStart + '-' + proc.locusEnd + ' ' + proc.procedureType;
    }).join(' and ');
  } else {
    caseDescription = (caseSummary.levels && caseSummary.levels !== '-') ?
      `${caseSummary.levels} ${caseSummary.name}` : caseSummary.name;
  }

  if (!shortForm) {
    caseDescription = caseSummary.isRevision ? `${caseDescription} Revision` : caseDescription;
    caseDescription = caseSummary.isStaged && caseSummary.stage ?
      `${caseDescription} Part ${caseSummary.stage}` :
      caseDescription;
  }

  return caseDescription;
};

/**
 * Formats the surgical team display from an array of CaseActor objects.
 *
 * @param actors Array of CaseActor objects.
 * @returns Formatted string of actor names or '-' if none.
 */
export const formatSurgicalTeamDisplay = (actors: CaseActor[]): string => {
  return (actors && actors.length) ? actors.map(actor => actor.actorName).join(', ') : '-';
};

/** Maps display labels (including legacy) to ActivityNames. Returns input if already an ActivityNames value. */
export const getSegmentKeyByLabel = (label: string): string => {
  // Handle legacy display labels
  if (label === 'Decompression & Alignment') return ActivityNames.DECOMPRESSION_ALIGNMENT;
  if (label === 'Post Cannulation Activities') return ActivityNames.POST_CANNULATION;
  if (label === 'Cannulation & Screw Placement') return ActivityNames.CANNULATION_SCREW_PLACEMENT;
  // Return as-is (already an ActivityNames value or unknown)
  return label;
};

/** Get display label for an activity. */
export const getActivityLabel = (activityName: string): string => {
  return ACTIVITY_LABEL[activityName] ?? activityName;
};

/** Returns CSS variable color for activity name. Preferred for modern chart components. */
export const getActivityColor = (activityName: string): string => {
  return ACTIVITY_COLORS[activityName] || 'var(--chart-5)';
};

// TODO: Remove once we upgrade to v1.2
/** @deprecated Returns hex color for step name. Do not use for any new code; use getActivityColor going forward. */
export const getStepColor = (stepName: string): string => {
  return STEP_COLORS_LEGACY[stepName] || getActivityColor(stepName);
};

export const sortCases = (caseList: CaseReportSummary[], field:string, direction:string):
  CaseReportSummary[] => {
  const sortedCases = [...caseList].sort((a, b) => {
    if (field === 'date') {
      return direction === 'asc'
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : new Date(b.date).getTime() - new Date(a.date).getTime();
    } else if (field === 'name') {
      return direction === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else if (field === 'levels') {
      return direction === 'asc'
        ? a.levels.localeCompare(b.levels)
        : b.levels.localeCompare(a.levels);
    }
    return 0;
  });

  return sortedCases;
};
